import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  BookOpen,
  Search,
  Volume2,
  ChevronRight,
  Brain,
  Filter,
  RotateCcw,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { speakText as speakWithBestVoice } from '../utils/speech';

const HSK_LEVELS = [
  { level: 1, label: 'HSK 1', color: 'from-green-400 to-emerald-500', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600' },
  { level: 2, label: 'HSK 2', color: 'from-blue-400 to-cyan-500', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600' },
  { level: 3, label: 'HSK 3', color: 'from-yellow-400 to-amber-500', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-600' },
  { level: 4, label: 'HSK 4', color: 'from-orange-400 to-orange-500', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600' },
  { level: 5, label: 'HSK 5', color: 'from-red-400 to-rose-500', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600' },
  { level: 6, label: 'HSK 6', color: 'from-purple-400 to-violet-500', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600' },
  { level: 7, label: 'HSK 7-9', color: 'from-pink-400 to-fuchsia-500', bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-600' },
];

export default function Dictionary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [selectedDictionary, setSelectedDictionary] = useState(
    () => location.state?.dictionaryType || localStorage.getItem('dictionaryType') || 'hsk'
  );
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [expandedCard, setExpandedCard] = useState(null);
  const [levelStats, setLevelStats] = useState({});
  const [resetting, setResetting] = useState(null);

  useEffect(() => {
    fetchLevelStats();
  }, []);

  useEffect(() => {
    localStorage.setItem('dictionaryType', selectedDictionary);
  }, [selectedDictionary]);

  const fetchLevelStats = async () => {
    try {
      const stats = {};
      for (const lvl of HSK_LEVELS) {
        const res = await api.get(`/api/cards/level/${lvl.level}?limit=1`);
        stats[lvl.level] = res.data.total_count;
      }
      setLevelStats(stats);
    } catch (error) {
      console.error('Failed to load level stats');
    }
  };

  const fetchCards = async (level, searchQuery = '', dictionary = selectedDictionary) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100', offset: '0' });
      if (searchQuery) params.append('search', searchQuery);

      const endpoint = dictionary === 'ielts'
        ? '/api/cards/'
        : `/api/cards/level/${level}`;
      if (dictionary === 'ielts') {
        params.append('dictionary_type', 'ielts');
      }
      const res = await api.get(`${endpoint}?${params}`);
      setCards(res.data.cards);
      setTotalCount(res.data.total_count);
      setSelectedLevel(level);
    } catch (error) {
      toast.error('Failed to load dictionary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDictionary === 'ielts') {
      fetchCards(null, search, 'ielts');
    }
  }, [selectedDictionary]);

  const handleLevelSelect = (level) => {
    setExpandedCard(null);
    fetchCards(level, search);
  };

  const handleDictionarySelect = (dictionary) => {
    setSelectedDictionary(dictionary);
    setSelectedLevel(dictionary === 'ielts' ? 'ielts' : null);
    setExpandedCard(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (selectedDictionary === 'ielts' || selectedLevel) {
      fetchCards(selectedLevel, search);
    }
  };

  const handleReset = async (cardId) => {
    setResetting(cardId);
    try {
      await api.post(`/api/cards/${cardId}/reset`);
      toast.success('Card reset successfully');
      if (selectedDictionary === 'ielts' || selectedLevel) {
        fetchCards(selectedLevel, search);
      }
    } catch (error) {
      toast.error('Failed to reset card');
    } finally {
      setResetting(null);
    }
  };

  const speakWord = (text) => {
    const language = selectedDictionary === 'ielts' ? 'en-US' : 'zh-CN';
    speakWithBestVoice(text, language);
  };

  const getMasteryBadge = (card) => {
    if (card.repetitions === 0 && !card.last_reviewed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium border border-slate-200">
          <AlertCircle className="w-3 h-3" />
          New
        </span>
      );
    }
    if (card.repetitions < 3) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-600 rounded-full text-xs font-medium border border-amber-200">
          <Clock className="w-3 h-3" />
          Learning
        </span>
      );
    }
    if (card.interval >= 21) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-600 rounded-full text-xs font-medium border border-emerald-200">
          <CheckCircle className="w-3 h-3" />
          Mastered
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-medium border border-blue-200">
        <Brain className="w-3 h-3" />
        Review
      </span>
    );
  };

  const currentLevelConfig = HSK_LEVELS.find(l => l.level === selectedLevel);
  const showingCards = selectedDictionary === 'ielts' || selectedLevel;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between max-w-7xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 transition-colors bg-white/60 backdrop-blur-sm px-3 py-2 rounded-xl border border-slate-200 hover:border-indigo-200"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Vocabulary Dictionaries
          </h1>
        </div>
        <div className="w-20" />
      </div>

      <div className="px-6 pb-8 max-w-7xl mx-auto">
        {/* Dictionary Selection */}
        {!selectedDictionary && !loading && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-800 mb-2">Choose a Dictionary</h2>
              <p className="text-slate-500">Select a language before browsing vocabulary</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleDictionarySelect('hsk')}
                className="bg-white rounded-2xl shadow-sm border-2 border-indigo-200 overflow-hidden hover:shadow-lg transition-all text-left"
              >
                <div className="bg-gradient-to-br from-indigo-500 to-violet-600 p-6 text-white">
                  <h3 className="text-2xl font-bold">HSK Chinese</h3>
                  <p className="text-white/80 mt-1">Chinese vocabulary by HSK level</p>
                </div>
                <div className="p-5 text-slate-600">Browse HSK 1 through HSK 7-9</div>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleDictionarySelect('ielts')}
                className="bg-white rounded-2xl shadow-sm border-2 border-emerald-200 overflow-hidden hover:shadow-lg transition-all text-left"
              >
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-white">
                  <h3 className="text-2xl font-bold">IELTS English</h3>
                  <p className="text-white/80 mt-1">English vocabulary for IELTS study</p>
                </div>
                <div className="p-5 text-slate-600">Browse the complete IELTS vocabulary list</div>
              </motion.button>
            </div>
          </div>
        )}

        {/* HSK Level Selection */}
        {selectedDictionary === 'hsk' && !selectedLevel && !loading && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-800 mb-2">Select HSK Level</h2>
              <p className="text-slate-500">Browse vocabulary by proficiency level</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {HSK_LEVELS.map((lvl) => (
                <motion.button
                  key={lvl.level}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleLevelSelect(lvl.level)}
                  className={`bg-white rounded-2xl shadow-sm border-2 ${lvl.border} overflow-hidden hover:shadow-lg transition-all`}
                >
                  <div className={`bg-gradient-to-br ${lvl.color} p-4 text-white text-center`}>
                    <h3 className="text-xl font-bold">{lvl.label}</h3>
                  </div>
                  <div className={`p-4 text-center ${lvl.bg}`}>
                    <p className={`text-2xl font-bold ${lvl.text}`}>
                      {levelStats[lvl.level] || 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">words</p>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Dictionary View */}
        {showingCards && (
          <div className="space-y-4">
            {/* Level Header */}
            <div className={`bg-gradient-to-br ${currentLevelConfig?.color || 'from-indigo-400 to-violet-500'} rounded-2xl p-6 text-white shadow-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <button
                    onClick={() => {
                      setSelectedDictionary(null);
                      setSelectedLevel(null);
                      setCards([]);
                    }}
                    className="text-white/80 hover:text-white text-sm mb-2 inline-block"
                  >
                    ← Back to levels
                  </button>
                  <h2 className="text-3xl font-bold">
                    {selectedDictionary === 'ielts' ? 'IELTS English Dictionary' : `${currentLevelConfig?.label || `HSK ${selectedLevel}`} Dictionary`}
                  </h2>
                  <p className="text-white/80 mt-1">
                    {totalCount} words in this dictionary
                  </p>
                </div>
                <button
                  onClick={() => navigate('/study', {
                    state: {
                      dictionaryType: selectedDictionary,
                      ...(selectedDictionary === 'hsk' ? { hskLevel: selectedLevel } : {}),
                      dailyReviewLimit: user?.daily_review_limit || Number(localStorage.getItem('dailyReviewLimit')) || 50
                    }
                  })}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2"
                >
                  <Brain className="w-5 h-5" />
                  {selectedDictionary === 'ielts' ? 'Study IELTS Words' : 'Study This Level'}
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by word, pinyin, or meaning..."
                className="w-full pl-12 pr-4 py-4 bg-white rounded-2xl border-2 border-slate-200 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 outline-none transition-all text-lg shadow-sm"
              />
            </form>

            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
              </div>
            )}

            {/* Cards List */}
            {!loading && (
              <div className="space-y-2">
                {cards.map((card, index) => (
                  <motion.div
                    key={card.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.02, 0.5) }}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md hover:border-indigo-200 transition-all"
                  >
                    <div
                      className="px-4 py-3 flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedCard(expandedCard === card.id ? null : card.id)}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <h3 className="text-2xl font-bold text-slate-800">{card.word}</h3>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-400">{card.pinyin}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  speakWord(card.word);
                                }}
                                className="p-1 text-indigo-400 hover:text-indigo-600 transition-colors"
                              >
                                <Volume2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {card.meaning_en && (
                              <span className="text-sm text-blue-600 truncate">{card.meaning_en}</span>
                            )}
                            {card.meaning_vi && (
                              <span className="text-sm text-emerald-600 truncate">
                                {card.meaning_en && ' • '}
                                {card.meaning_vi}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        {getMasteryBadge(card)}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReset(card.id);
                          }}
                          disabled={resetting === card.id}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all disabled:opacity-50"
                          title="Reset card"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <motion.div
                          animate={{ rotate: expandedCard === card.id ? 90 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </motion.div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {expandedCard === card.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                          <div className="border-t border-slate-200 bg-slate-50">
                            <div className="px-4 py-4 space-y-3">
                              {card.part_of_speech && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">Part of Speech</span>
                                  <span className="px-2 py-1 bg-indigo-100 text-indigo-600 rounded-md text-xs font-medium">{card.part_of_speech}</span>
                                </div>
                              )}

                              {card.example_sentence && (
                                <div className="bg-white rounded-lg p-3 border border-slate-200">
                                  <p className="text-slate-800 font-medium">{card.example_sentence}</p>
                                  {card.example_sentence_pinyin && (
                                    <p className="text-sm text-blue-600 mt-1">{card.example_sentence_pinyin}</p>
                                  )}
                                  {card.example_translation_vi && (
                                    <p className="text-sm text-emerald-600 mt-1">{card.example_translation_vi}</p>
                                  )}
                                </div>
                              )}

                              <div className="grid grid-cols-3 gap-3">
                                <div className="bg-white rounded-lg p-2 border border-slate-200 text-center">
                                  <p className="text-[10px] text-slate-500 uppercase">Ease</p>
                                  <p className="text-sm font-bold text-indigo-600">{card.ease_factor?.toFixed(2)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-slate-200 text-center">
                                  <p className="text-[10px] text-slate-500 uppercase">Interval</p>
                                  <p className="text-sm font-bold text-indigo-600">{card.interval}d</p>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-slate-200 text-center">
                                  <p className="text-[10px] text-slate-500 uppercase">Reviews</p>
                                  <p className="text-sm font-bold text-indigo-600">{card.repetitions}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}

                {cards.length === 0 && !loading && (
                  <div className="text-center py-20">
                    <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-700 mb-2">No words found</h3>
                    <p className="text-slate-500">Try a different search term</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
